// assets/byte-pdp-mask-reveal.js

if (!customElements.get('byte-pdp-mask-reveal')) {
  // Hepsi hero yüksekliğinin oranı (scroll mesafesi)
  // MASK_OPEN: mask bu scroll'da tamamen açılır, galeri bu süre hero'ya yanaşmaz
  // MASK_OPEN_VERTICAL: dikey (tüp) üründe akış biraz daha uzun scroll'a yayılır;
  //   FADE_START'tan küçük kalmalı ki akış fade başlamadan bitsin
  // PIN: hero bu scroll'a kadar yerinde pinli kalır (galeri üstüne çıkar), sonra kayar
  // FADE_START / FADE_DISTANCE: fade nerede başlar, ne kadar sürede biter
  // Dikey (tüp) sahne: 1) normal scroll, görsel ekran ortasına gelir
  // 2) görsel ortada sabit, FLOW_VERTICAL scroll boyunca ezme akar
  // 3) akış bitince görsel bırakılır, sayfa normal akar (fade / üst üste binme yok)
  const MASK_OPEN_RATIO = 0.3;
  const FLOW_VERTICAL_RATIO = 0.35;
  const PIN_DISTANCE_RATIO = 1;
  const FADE_START_RATIO = 0.5;
  const FADE_DISTANCE_RATIO = 0.8;
  const VERTICAL_CLASS = 'byte-pdp-mask-reveal--vertical';
  const VERTICAL_HERO_CLASS = 'is-mask-vertical';

  class BytePdpMaskReveal extends HTMLElement {
    constructor() {
      super();

      this.isTicking = false;
      this.isVertical = false;
      this.currentShift = 0;
      this.heroItem = null;
      this.galleryGrid = null;
      this.wrappedImage = null;
      this.headerOffset = 0;
      this.desktopQuery = window.matchMedia('(min-width: 1024px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.boundHandleScroll = this.handleScroll.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
      this.boundHandleWrappedImageLoad = this.handleWrappedImageLoad.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      if (!this.heroItem || !this.galleryGrid) return;

      this.#resolveOrientation();

      if (this.reducedMotionQuery.matches) {
        this.style.setProperty('--byte-mask-progress', '1');
        return;
      }

      this.#bindEvents();
      this.#update();
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    handleScroll() {
      if (this.isTicking) return;

      this.isTicking = true;
      requestAnimationFrame(() => {
        this.isTicking = false;
        this.#update();
      });
    }

    handleResize() {
      this.handleScroll();
    }

    handleWrappedImageLoad() {
      this.#detectOrientationFromImage();
      this.handleScroll();
    }

    #cacheElements() {
      this.heroItem = this.closest('[data-grid-item]');
      this.galleryGrid = this.closest('[data-byte-mask-reveal]');
      this.wrappedImage = this.querySelector('.byte-pdp-mask-reveal__layer--wrapped img');
      this.headerOffset = Number(this.dataset.headerOffset) || 0;
    }

    #bindEvents() {
      window.addEventListener('scroll', this.boundHandleScroll, { passive: true });
      window.addEventListener('resize', this.boundHandleResize);
    }

    #unbindEvents() {
      window.removeEventListener('scroll', this.boundHandleScroll);
      window.removeEventListener('resize', this.boundHandleResize);
      this.wrappedImage?.removeEventListener('load', this.boundHandleWrappedImageLoad);
    }

    // Liquid yön bilgisini veremediyse ('auto') görselin gerçek boyutundan karar verilir.
    #resolveOrientation() {
      const maskOrientation = this.dataset.maskOrientation || 'auto';

      if (maskOrientation !== 'auto') {
        this.isVertical = maskOrientation === 'vertical';
        this.classList.toggle(VERTICAL_CLASS, this.isVertical);
        this.heroItem?.classList.toggle(VERTICAL_HERO_CLASS, this.isVertical);
        return;
      }

      if (!this.wrappedImage) return;

      if (this.wrappedImage.complete && this.wrappedImage.naturalWidth) {
        this.#detectOrientationFromImage();
        return;
      }

      this.wrappedImage.addEventListener('load', this.boundHandleWrappedImageLoad, { once: true });
    }

    #detectOrientationFromImage() {
      const { naturalWidth, naturalHeight } = this.wrappedImage;
      if (!naturalWidth || !naturalHeight) return;

      this.isVertical = naturalHeight > naturalWidth;
      this.classList.toggle(VERTICAL_CLASS, this.isVertical);
      this.heroItem?.classList.toggle(VERTICAL_HERO_CLASS, this.isVertical);
    }

    #clamp(value) {
      return Math.min(1, Math.max(0, value));
    }

    #update() {
      const heroHeight = this.heroItem.offsetHeight;
      if (!heroHeight) return;

      const isDesktop = this.desktopQuery.matches;

      if (this.isVertical) {
        this.#updateVertical(heroHeight, isDesktop);
        return;
      }

      const gridTop = this.galleryGrid.getBoundingClientRect().top;
      const scrollDelta = Math.max(0, this.headerOffset - gridTop);

      const maskOpenDistance = heroHeight * MASK_OPEN_RATIO;
      const maskProgress = this.#clamp(scrollDelta / maskOpenDistance);
      this.style.setProperty('--byte-mask-progress', maskProgress.toFixed(3));

      if (!isDesktop) {
        this.heroItem.style.removeProperty('--byte-mask-shift');
        this.heroItem.style.removeProperty('--byte-mask-fade');
        this.heroItem.style.removeProperty('--byte-mask-pin-space');
        this.heroItem.classList.remove('is-faded');
        return;
      }

      // Mask açılırken galeri hero'ya yanaşmasın diye altına kısa bir boşluk açılır
      this.heroItem.style.setProperty('--byte-mask-pin-space', `${maskOpenDistance.toFixed(1)}px`);

      // Pin: scroll kadar aşağı translate = yerinde durur, galeri üstüne çıkar.
      // Pin bitince sabit kalır = doğal scroll'la yukarı kayar
      const pinOffset = Math.min(scrollDelta, heroHeight * PIN_DISTANCE_RATIO);

      const fadeProgress = this.#clamp(
        (scrollDelta - heroHeight * FADE_START_RATIO) / (heroHeight * FADE_DISTANCE_RATIO)
      );

      this.heroItem.style.setProperty('--byte-mask-shift', `${pinOffset.toFixed(1)}px`);
      this.heroItem.style.setProperty('--byte-mask-fade', (1 - fadeProgress).toFixed(3));
      this.heroItem.classList.toggle('is-faded', fadeProgress >= 1);
    }

    #updateVertical(heroHeight, isDesktop) {
      const viewportHeight = window.innerHeight;
      const visibleHeight = viewportHeight - this.headerOffset;

      // Transform'suz (doğal) konum: ölçülen top'tan bizim verdiğimiz shift düşülür
      const heroNaturalTop = this.heroItem.getBoundingClientRect().top - this.currentShift;
      const heroNaturalDocCenter = heroNaturalTop + window.scrollY + heroHeight / 2;

      // Görsel ekrana sığıyorsa ortalanır; sığmıyorsa üstten hizalanır → tüp
      // akış boyunca tamamen görünür kalır
      const targetTop = heroHeight <= visibleHeight
        ? this.headerOffset + (visibleHeight - heroHeight) / 2
        : this.headerOffset;
      const targetCenter = targetTop + heroHeight / 2;

      // Akış ekranın altında durur (görselin ekran dışı kalan kısmına akmaz).
      // Akış bitince 1'e çekilir: o anda kalan kısım zaten ekran dışında,
      // sonra sayfa kayarken akışın devamı kesik görünmez.
      const visibleFlowEnd = Math.min(1, (viewportHeight - targetTop) / heroHeight);

      // Bu scroll noktasında görsel tam ortaya gelir; sayfa zaten bunun altındaysa 0
      const centerScroll = Math.max(0, heroNaturalDocCenter - targetCenter);
      const scrolledPastCenter = Math.max(0, window.scrollY - centerScroll);

      const flowDistance = heroHeight * FLOW_VERTICAL_RATIO;
      const flowProgress = this.#clamp(scrolledPastCenter / flowDistance);
      const flowEnd = flowProgress >= 1 ? 1 : visibleFlowEnd;
      this.style.setProperty('--byte-mask-progress', flowProgress.toFixed(3));
      this.style.setProperty('--byte-mask-flow-end', flowEnd.toFixed(3));

      this.heroItem.style.setProperty('--byte-mask-fade', '1');
      this.heroItem.classList.remove('is-faded');

      if (!isDesktop) {
        this.currentShift = 0;
        this.heroItem.style.removeProperty('--byte-mask-shift');
        this.heroItem.style.removeProperty('--byte-mask-pin-space');
        return;
      }

      // Akış boyunca görsel ortada sabit; altındaki boşluk pin mesafesi kadar
      // ayrılır → galeri görselin üstüne binmez, akış bitince birlikte kayarlar
      this.currentShift = Math.min(scrolledPastCenter, flowDistance);
      this.heroItem.style.setProperty('--byte-mask-pin-space', `${flowDistance.toFixed(1)}px`);
      this.heroItem.style.setProperty('--byte-mask-shift', `${this.currentShift.toFixed(1)}px`);
    }
  }

  customElements.define('byte-pdp-mask-reveal', BytePdpMaskReveal);
}